import React, { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cross2Icon } from '@radix-ui/react-icons';
import { createPortal } from 'react-dom';
import { useTheme } from '../../Theme/ThemeProvider';

interface TranslationQuotaAlertProps {
  limit?: number | undefined;
  remaining?: number | undefined;
  onClose: () => void;
  open: boolean;
  resetTimestamp?: string;
  overlay?: boolean;
  bottomOffset?: number;
  source?: 'server' | 'local';
}

const buildMessage = (remaining?: number, limit?: number) => {
  if (typeof remaining === 'number' && typeof limit === 'number') {
    const used = Math.max(limit - remaining, 0);
    return `${used}/${limit} translations used`;
  }
  if (typeof limit === 'number') return `0/${limit} translations used`;
  return 'Translation quota';
};

export const TranslationQuotaAlert: React.FC<TranslationQuotaAlertProps> = ({
  limit,
  remaining,
  onClose,
  open,
  resetTimestamp,
  overlay = true,
  bottomOffset = 110,
}) => {
  const { mode } = useTheme();

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 15000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const radius = 20;
  const stroke = 5;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progressRatio = (typeof remaining === 'number' && typeof limit === 'number') ? Math.min((limit - remaining) / Math.max(limit, 1), 1) : 0;
  const strokeDashoffset = useMemo(() => circumference * (1 - progressRatio), [circumference, progressRatio]);

  if (!open) return null;

  const body = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="flex justify-center bg-transparent"
        style={overlay ? { pointerEvents: 'none' } : undefined}
      >
        <div
          className="relative flex items-center gap-4 rounded-lg px-4 py-3 border shadow-sm max-w-sm"
          style={{
            background: mode === 'dark' ? '#202024' : 'var(--color-secondary)',
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)',
            color: 'var(--color-text)',
            pointerEvents: 'auto'
          }}
          role="status"
          aria-live="polite"
        >
          <div className="relative" style={{ width: radius * 2, height: radius * 2 }}>
            <svg height={radius * 2} width={radius * 2}>
              <circle
                stroke={mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'}
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
              <circle
                stroke={'var(--color-accent)'}
                fill="transparent"
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={`${circumference} ${circumference}`}
                style={{
                  strokeDashoffset,
                  transition: 'stroke-dashoffset 0.6s ease',
                  transform: 'rotate(-180deg)',
                  transformOrigin: '50% 50%'
                }}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold select-none">
              {typeof remaining === 'number' && typeof limit === 'number' ? Math.max(limit - remaining, 0) : '-'}
            </div>
          </div>
          <div className="flex-1 text-sm font-medium flex flex-col gap-1">
            <span>{buildMessage(remaining, limit)}</span>
            {resetTimestamp && (() => {
              const dt = new Date(resetTimestamp);
              if (isNaN(dt.getTime())) return null;
              const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
              const timePart = dt.toLocaleTimeString(undefined, opts);
              const datePart = dt.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
              return <span className="text-[11px] opacity-80">Resets at {timePart} on {datePart}</span>;
            })()}
          </div>
          <button
            aria-label="Close translation quota alert"
            className="p-1 rounded hover:bg-[var(--button-background-color)] transition-colors"
            onClick={onClose}
          >
            <Cross2Icon />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  if (!overlay) return body;

  return createPortal(
    <div style={{ position: 'fixed', bottom: `${bottomOffset}px`, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 90 }}>
      {body}
    </div>,
    document.body
  );
};

export default TranslationQuotaAlert;
